import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { Fragment } from 'react'
import Link from 'next/link'
import { ChevronRight } from 'lucide-react'

interface Crumb {
    href?: string
    label: ReactNode
}

interface PageHeroProps {
    breadcrumbs: Crumb[]
    title: ReactNode
    intro?: ReactNode
    icon?: LucideIcon
    children?: ReactNode
}

export const PageHero: FC<PageHeroProps> = ({ breadcrumbs, title, intro, icon: Icon, children }): ReactNode => {
    return (
        <section className='relative overflow-hidden border-b border-border/70'>
            <div className='bg-grid pointer-events-none absolute inset-0' aria-hidden='true' />
            <div className='hero-glow pointer-events-none absolute inset-0' aria-hidden='true' />

            <div className='relative mx-auto max-w-7xl px-4 pb-12 pt-8 sm:px-6 lg:px-8 lg:pb-14'>
                <nav aria-label='Breadcrumb'>
                    <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                        <li>
                            <Link href='/' className='hover:text-foreground'>
                                Home
                            </Link>
                        </li>

                        {breadcrumbs.map((crumb, index) => (
                            <Fragment key={index}>
                                <li aria-hidden='true'>
                                    <ChevronRight className='size-3.5' />
                                </li>

                                <li>
                                    {crumb.href
                                        ? (
                                            <Link href={crumb.href} className='hover:text-foreground'>
                                                {crumb.label}
                                            </Link>
                                        )
                                        : <span aria-current='page' className='text-foreground'>{crumb.label}</span>}
                                </li>
                            </Fragment>
                        ))}
                    </ol>
                </nav>

                <div className='mt-8 flex items-start gap-4'>
                    {Icon && (
                        <span className='hidden size-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/25 sm:inline-flex'>
                            <Icon className='size-6' aria-hidden='true' />
                        </span>
                    )}

                    <div className='min-w-0 max-w-3xl'>
                        <h1 className='font-heading text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl'>
                            {title}
                        </h1>

                        {intro && (
                            <div className='mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg'>
                                {intro}
                            </div>
                        )}
                    </div>
                </div>

                {children && <div className='mt-7'>{children}</div>}
            </div>
        </section>
    )
}
